import { api, type AppearanceRow, type PageNew } from '@setplex/tria-api'
import {
  attach,
  createEvent,
  createStore,
  sample,
  type EffectParams,
} from 'effector'
import { features, options } from '~/core/flags'
import { model as guest } from '~/features/subscriber/guest'
import { aye } from '~/shared/helpers'
import { logger } from '~/shared/logger'
import { APPEARANCE_DEFAULT_HOME_ROWS, resetFallbacks } from './defaults'
import {
  Awaiting,
  Dirty,
  Empty,
  Failed,
  Loading,
  Ready,
  Updating,
  isType,
  toDirty,
  toLoading,
  type Row,
} from './lib'

/*
 * Dynamic and lazy loading list of rows
 */

// first rows load request count
const INITIAL_LOAD_COUNT = 12

// next rows load requests count
const LAZY_LOAD_BY_COUNT = 12

export const loadRows = createEvent()
export const loadMoreRows = createEvent()
export const dirtyRows = createEvent()
export const resetRows = createEvent()

const getHomeRowsFx = attach({
  source: {
    features: features.$featuredFlags,
    options: options.$optionsFlags,
    isGuest: guest.$guest,
  },
  async effect(
    { features, options, isGuest },
    params: Required<EffectParams<typeof api.appearance.getHomeRowsFx>>
  ) {
    let rows: PageNew<AppearanceRow>

    // there are three possible cases:
    // - `appearanceCustomizationEnabled` is not defined
    //       ↳ that means we are on the old wbs,
    //         and we should use default configuration with fallback for some rows,
    //         because some api endpoints are not available in the old wbs
    // - `appearanceCustomizationEnabled` = true
    //       ↳ that means we are on the new wbs,
    //         and we should load rows configuration from the server
    // - `appearanceCustomizationEnabled` = false
    //       ↳ that means we are on the new wbs, but customization is disabled,
    //         and we should use default configuration without fallbacks,

    const grab = {
      enabled: async () => api.appearance.getHomeRowsFx(params),
      disabled: async () => APPEARANCE_DEFAULT_HOME_ROWS(),
      fallback: async () => APPEARANCE_DEFAULT_HOME_ROWS(true),
    }

    // if `appearanceCustomizationEnabled` is not defined - this is old wbs, use fallback
    if (features.appearanceCustomizationEnabled == null) {
      rows = await grab.fallback()
    }

    // if `appearanceCustomizationEnabled` is enabled - load rows from the server
    else if (features.appearanceCustomizationEnabled) {
      try {
        rows = await grab.enabled()
      } catch (error) {
        logger.error('Failed to load home rows:', error)

        // if this is the very first request - try to use default rows configuration as a fallback
        if (params == null || params.page === 0) {
          rows = await grab.disabled()
        } else {
          throw error
        }
      }
    }

    // if `appearanceCustomizationEnabled` is disabled - use default rows configuration
    else {
      rows = await grab.disabled()
    }

    // filter rows (default and custom both)
    rows.content = rows.content.filter((row) => {
      // filter rows, disabled by flags
      // prettier-ignore
      if (
        (row.contentType === 'TV_CHANNEL' && !options.tvEnabled) ||
        (row.contentType === 'TV_BUNDLE' && !options.tvEnabled) ||
        (row.contentType === 'TV_SHOW' && !options.tvShowEnabled) ||
        (row.contentType === 'TV_SHOW_BUNDLE' && !options.tvShowEnabled) ||
        (row.contentType === 'VOD' && !options.vodEnabled) ||
        (row.contentType === 'VOD_BUNDLE' && !options.vodEnabled) ||
        (row.contentType === 'LIVE_EVENT' && !options.liveEventEnabled) ||
        (row.contentType === 'PARTNER_PRODUCT' && !features.partnersProgramEnabled) ||
        (row.localizedTitleKey === 'movies_featured' && !options.featuredCarouselsEnabled) ||
        (row.localizedTitleKey === 'series_featured' && !options.featuredCarouselsEnabled) ||
        (row.localizedTitleKey === 'channel_featured' && !options.featuredCarouselsEnabled)
      ) return false

      // filter rows, unavailable for guests
      // prettier-ignore
      if (isGuest && (
        row.localizedTitleKey === 'continue_watching' ||
        row.localizedTitleKey === 'live_channels_recently_watched' ||
        row.localizedTitleKey === 'channel_favorites' ||
        row.localizedTitleKey === 'movies_favorite' ||
        row.localizedTitleKey === 'series_favorite'
      )) return false

      return true
    })

    return rows
  },
})

const $loadedRowsCount = createStore(0).reset(resetRows)
export const $rows = createStore<Row[]>([]).reset(resetRows)

export const $isRowsLoading = getHomeRowsFx.pending

export const $isRowsLoaded = createStore(false)
  .on(getHomeRowsFx.doneData, (loaded, page) => loaded || aye(page.last))
  .reset(resetRows)

export const $isBannersLoading = api.banners.getBannersFx.pending

// reset defaults on reset rows event
sample({
  clock: resetRows,
  target: resetFallbacks,
})

// initial rows load request
sample({
  clock: loadRows,
  source: $loadedRowsCount,
  filter: (loadedRowsCount) => loadedRowsCount === 0,
  target: loadMoreRows,
})

// first and next rows load request
// - on the next requests loads page N with LAZY_LOAD_BY_COUNT rows
// - on the first request loads page 0 with INITIAL_LOAD_COUNT rows
sample({
  clock: loadMoreRows,
  source: {
    loadedRowsCount: $loadedRowsCount,
    isRowsLoaded: $isRowsLoaded,
    pending: getHomeRowsFx.pending,
  },
  filter: ({ pending, isRowsLoaded }) => !pending && !isRowsLoaded,
  fn: ({ loadedRowsCount }) => ({
    page: Math.trunc(loadedRowsCount / LAZY_LOAD_BY_COUNT),
    count: loadedRowsCount === 0 ? INITIAL_LOAD_COUNT : LAZY_LOAD_BY_COUNT,
  }),
  target: getHomeRowsFx,
})

sample({
  clock: getHomeRowsFx.done,
  fn: ({ params }) => (params ? params.page * params.count + params.count : 0),
  target: $loadedRowsCount,
})

// add loaded rows to the list, with empty data
sample({
  clock: getHomeRowsFx.doneData,
  source: $rows,
  fn: (rows, page) => {
    const res = rows.slice()
    for (const row of page.content) {
      if (res.find((r) => r.id === row.id) == null) {
        res.push({ id: row.id, desc: row, data: Empty() })
      }
    }
    return res
  },
  target: $rows,
})

// mark refreshable rows as dirty (or reset them to empty, if they are not ready)
sample({
  clock: dirtyRows,
  source: $rows,
  fn: (rows) => rows.map(toDirty),
  target: $rows,
})

/*
 * Dynamic and lazy loading of row data
 */

const MAX_PARALLEL_LOAD = 3

export const loadRow = createEvent<AppearanceRow>()

export const getRowFx = attach({
  effect: api.appearance.getRowFx,
})

const $loadRowQueue = createStore<AppearanceRow[]>([]).on(
  getRowFx,
  (queue, { id }) => queue.filter((row) => row.id !== id)
)

// load dirty rows on the page load
sample({
  clock: loadRows,
  source: { rows: $rows, queue: $loadRowQueue },
  filter: ({ rows }) =>
    rows.length > 0 && rows.some((r) => isType(r.data, Dirty)),
  fn: ({ rows, queue }) => [
    ...queue,
    ...rows.filter((r) => isType(r.data, Dirty)).map((r) => r.desc),
  ],
  target: $loadRowQueue,
})

// add row to the load queue if it is empty or dirty
// do not reload already loaded and not refreshable rows
sample({
  clock: loadRow,
  source: { rows: $rows, queue: $loadRowQueue },
  filter: ({ rows }, row) => {
    const data = rows.find((r) => r.id === row.id)?.data
    return data == null || isType(data, Empty) || isType(data, Dirty)
  },
  fn: ({ queue }, row) => [...queue, row],
  target: $loadRowQueue,
})

// watch load queue and start loading rows if there are free slots for parallel loading
sample({
  clock: [$loadRowQueue, getRowFx.inFlight],
  source: { queue: $loadRowQueue, inFlight: getRowFx.inFlight },
  filter: ({ queue, inFlight }) =>
    queue.length > 0 && inFlight < MAX_PARALLEL_LOAD,
  fn: ({ queue }) => queue[0],
  target: getRowFx,
})

// mark empty rows as loading, and dirty rows as updating
sample({
  clock: getRowFx,
  source: $rows,
  fn: (rows, { id }) => {
    const idx = rows.findIndex((r) => r.id === id)
    return idx === -1 ? rows : rows.toSpliced(idx, 1, toLoading(rows[idx]))
  },
  target: $rows,
  batch: false,
})

// get loaded row data and update row in the list
// skip updating if row's state was changed from loading|updating (for example user left the page)
sample({
  clock: getRowFx.done,
  source: $rows,
  filter: (rows, { params: { id } }) => {
    const data = rows.find((r) => r.id === id)?.data
    return data != null && (isType(data, Loading) || isType(data, Updating))
  },
  fn: (rows, { params: { id }, result: { items, last } }) => {
    rows = rows.slice()

    // first add in awaiting state
    const idx = rows.findIndex((r) => r.id === id)
    if (idx !== -1) {
      rows[idx] = {
        ...rows[idx],
        data: Awaiting(items),
        hasMore: !last,
      }
    }

    // update all awaiting rows to ready, if there are no loading rows before them
    // this allows to show rows in the order from top to bottom, without flickering
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (isType(row.data, Awaiting)) {
        rows[i] = {
          ...row,
          data: Ready(row.data.payload),
        }
      }
      if (isType(row.data, Loading)) break
    }

    return rows
  },
  target: $rows,
})

// update failed row in the list
// skip updating if row's state was changed from loading|updating (for example user left the page)
sample({
  clock: getRowFx.fail,
  source: $rows,
  filter: (rows, { params: { id } }) => {
    const data = rows.find((r) => r.id === id)?.data
    return data != null && (isType(data, Loading) || isType(data, Updating))
  },
  fn: (rows, { params: { id }, error }) => {
    return rows.map((row) => {
      if (row.id !== id) return row
      return {
        ...row,
        data: Failed(error),
      }
    })
  },
  target: $rows,
})

sample({
  clock: getRowFx.fail,
  fn: ({ params, error }) => ['failed to load row', params, ':', error],
  target: logger.errorFx,
})
